import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { PopInProps } from "@companion-core/shared/app/Components/PopIn/popIn.model";
import { UseRequestEditorProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestEditor/requestEditor.model";
import { useModifyRequest, useProposals } from "@companion-core/shared/app/Queries";
import { useRejectRecommendations } from "@companion-core/shared/app/Queries";
import { APIProposal } from "@companion-core/shared/app/Interfaces/API";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";
import { useMemberData } from "@companion-core/shared/app/Hooks/member";

export const useRequestEditor = ({
  requestData,
  action,
  loader,
  onClosePopIn,
}: UseRequestEditorProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: rejectRecommendations } = useRejectRecommendations();
  const { data: member } = useMemberData();
  const { mutateAsync: modifyRequest } = useModifyRequest();
  const { data: proposalsData, isPending: isRecommendationsLoading } = useProposals(
    requestData.id,
    requestData.status === "recommended"
  );
  const proposal = useMemo(
    () =>
      (proposalsData?.data?.results as APIProposal[])?.find(
        (proposition) => proposition.status === "generated"
      ),
    [proposalsData]
  );

  const [isPopInOpen, setIsPopInOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [popInArgs, setPopInArgs] = useState<PopInProps | null>(null);
  const initialForm = useMemo(() => loadFormFromPath("common-forms/myRequests/detail/modify"), []);
  const popInInitialArgs: PopInProps = useMemo(
    () => ({
      testID: `request-detail__${action}-popin`,
      primaryButton: {
        text: t("request.my-requests.detail.popin.primary-button.text"),
        onClick: () => {
          setIsPopInOpen(false);
          onClosePopIn();
        },
        onPress: () => {
          setIsPopInOpen(false);
          onClosePopIn();
        },
      },
      onClose: () => {
        setIsPopInOpen(false);
        onClosePopIn();
      },
    }),
    [action]
  );

  const sendModificationRequest = useCallback(
    async (payload: Record<string, unknown>) => {
      setPopInArgs({
        testID: popInInitialArgs.testID,
        content: loader,
        freeze: true,
      });
      setIsLoading(true);
      try {
        if (action === "modify") {
          await modifyRequest({
            id: requestData.id,
            family: requestData.family ?? "",
            payload: { comment: payload.message as string },
          });
        } else {
          if (isRecommendationsLoading || !proposal) return;
          await rejectRecommendations({
            requestId: requestData.id,
            proposalId: proposal.id,
            payload: { comment: payload.message },
          });
        }
        await queryClient.invalidateQueries({ queryKey: ["my-requests"] });
        await queryClient.invalidateQueries({ queryKey: ["request"] });
        setPopInArgs({
          ...popInInitialArgs,
          icon: "JP-Circle-check",
          title: t(`request.my-requests.detail.popin.${action}.title`),
        });
      } catch {
        setPopInArgs({
          ...popInInitialArgs,
          icon: "JP-Circle-close",
          title: t(`request.my-requests.detail.popin.${action}-error.title`),
        });
      } finally {
        setIsLoading(false);
        setIsPopInOpen(true);
      }
    },
    [requestData, action, queryClient, popInInitialArgs, t, isRecommendationsLoading]
  );

  return {
    t,
    isLoading,
    isRecommendationsLoading,
    isPopInOpen,
    popInArgs,
    member,
    initialForm,
    sendModificationRequest,
  };
};
